import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
	LANGUAGES,
	getLanguageByCode,
	saveSelectedLanguage,
} from '../../constant/languages';
import './language-selector.scss';

const LanguageSelector = forwardRef(({ compact = false, hideLabelOnMobile = false, onOpen }, ref) => {
	const { t, i18n } = useTranslation();
	const { pathname } = useLocation();
	const [selectedLanguage, setSelectedLanguage] = useState(() => getLanguageByCode(i18n.language));
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef(null);

	useImperativeHandle(ref, () => ({
		close: () => setIsOpen(false),
	}));

	useEffect(() => {
		setSelectedLanguage(getLanguageByCode(i18n.language));
	}, [i18n.language]);

	useEffect(() => {
		setIsOpen(false);
	}, [pathname]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleLanguageChange = (language) => {
		i18n.changeLanguage(language.langCode);
		setSelectedLanguage(language);
		saveSelectedLanguage(language);
		setIsOpen(false);
	};

	const toggleOpen = () => {
		setIsOpen((current) => {
			const next = !current;
			if (next && onOpen) onOpen();
			return next;
		});
	};

	const labelClassName = [
		'language-selector__label',
		hideLabelOnMobile ? 'language-selector__label--hidden-mobile' : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div
			ref={containerRef}
			className={`language-selector${isOpen ? ' language-selector--open' : ''}${compact ? ' language-selector--compact' : ''}`}
		>
			<button
				type="button"
				className="language-selector__trigger"
				onClick={toggleOpen}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
			>
				<img
					alt={selectedLanguage.name}
					src={selectedLanguage.img}
					className="language-selector__flag"
				/>
				<span className={labelClassName}>{t(selectedLanguage.name)}</span>
				<img
					alt=""
					src="https://d2lxkizvrhu4im.cloudfront.net/icons/arrow-down_yellow_light_icon.svg.svg"
					className="language-selector__arrow"
				/>
			</button>
			<div className="language-selector__menu" role="listbox">
				{LANGUAGES.map((language) => (
					<button
						key={language.langCode}
						type="button"
						className={`language-selector__option${language.langCode === selectedLanguage.langCode ? ' language-selector__option--active' : ''}`}
						onClick={() => handleLanguageChange(language)}
						role="option"
						aria-selected={language.langCode === selectedLanguage.langCode}
					>
						<img alt="" src={language.img} className="language-selector__option-flag" />
						<span>{t(language.name)}</span>
					</button>
				))}
			</div>
		</div>
	);
});

LanguageSelector.displayName = 'LanguageSelector';

export default LanguageSelector;
